import React from 'react';

import { Box, IconButton, Typography, useMediaQuery } from '@mui/material';
import Modal from 'components/ModalV2';
import Button from 'components/V2/Button';
import PropTypes from 'prop-types';

import styles from './announcementModal.module.css';

import { ReactComponent as ArrowRightIcon } from 'asset/IconsPack/at_arrow-right-filled.svg';
import { ReactComponent as SparkleIcon } from 'asset/IconsPack/at_star-06-filled.svg';
import { ReactComponent as CloseIcon } from 'asset/IconsPack/at_x-close.svg';

import AnnouncementHighlight from '../AnnouncementHighlight';
import AnnouncementVideo from '../AnnouncementVideo';

const AnnouncementModal = ({ open, announcement, isMobile, onClose, onPrimaryAction }) => {
    const {
        media,
        title,
        subtitle,
        description,
        highlights = [],
        primaryAction,
        secondaryLabel,
    } = announcement;
    // Reduced motion gets the still poster instead of the loop.
    const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
    const showVideo = media.type === 'video' && !prefersReducedMotion;

    return (
        <Modal open={open} onClose={onClose} className={styles.modal} disableBackdropClick>
            <Box className={styles.hero}>
                <Box className={styles.heroBar}>
                    <Box component="span" className={styles.newTag}>
                        <SparkleIcon className={styles.newTagIcon} />
                        New
                    </Box>
                    <IconButton aria-label="Close" onClick={onClose} className={styles.close}>
                        <CloseIcon className={styles.closeIcon} />
                    </IconButton>
                </Box>
                {showVideo ? (
                    <AnnouncementVideo
                        src={media.src}
                        poster={media.poster}
                        className={styles.media}
                    />
                ) : (
                    <img
                        className={styles.media}
                        src={prefersReducedMotion && media.poster ? media.poster : media.src}
                        alt={media.alt}
                    />
                )}
            </Box>
            <Box className={styles.content}>
                <Typography component="h2" className={styles.title}>
                    {title}
                </Typography>
                {subtitle && <Typography className={styles.subtitle}>{subtitle}</Typography>}
                <Typography className={styles.description}>{description}</Typography>
                {highlights.length > 0 && (
                    <Box component="ul" className={styles.highlights}>
                        {highlights.map(({ icon, label }) => (
                            <AnnouncementHighlight key={label} icon={icon} label={label} />
                        ))}
                    </Box>
                )}
            </Box>
            <Box className={styles.actions}>
                {secondaryLabel && (
                    <Button
                        intent="secondary"
                        size={isMobile ? 'md' : 'lg'}
                        onClick={onClose}
                        className={styles.action}
                    >
                        {secondaryLabel}
                    </Button>
                )}
                <Button
                    intent="primary"
                    size={isMobile ? 'md' : 'lg'}
                    onClick={onPrimaryAction}
                    endIcon={<ArrowRightIcon className={styles.arrowIcon} />}
                    className={styles.action}
                >
                    {primaryAction.label}
                </Button>
            </Box>
        </Modal>
    );
};

AnnouncementModal.propTypes = {
    open: PropTypes.bool.isRequired,
    announcement: PropTypes.shape({
        // A video needs a poster, which stands in for it under reduced motion.
        media: PropTypes.shape({
            type: PropTypes.oneOf(['image', 'video']),
            src: PropTypes.string.isRequired,
            poster: PropTypes.string,
            alt: PropTypes.string.isRequired,
        }).isRequired,
        title: PropTypes.string.isRequired,
        subtitle: PropTypes.string,
        description: PropTypes.string.isRequired,
        highlights: PropTypes.arrayOf(
            PropTypes.shape({ icon: PropTypes.elementType, label: PropTypes.string })
        ),
        primaryAction: PropTypes.shape({
            label: PropTypes.string.isRequired,
            to: PropTypes.string.isRequired,
        }).isRequired,
        secondaryLabel: PropTypes.string,
    }).isRequired,
    isMobile: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onPrimaryAction: PropTypes.func.isRequired,
};

export default AnnouncementModal;
